import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../../../components/CustomButton';
import { useImportPlanMutation, usePreviewPlanImportMutation } from '../../../../api/organization/organizationApiSlice';
import type {
    PlanImportArgs,
    PlanImportProblem,
    PlanImportReport,
    Season,
} from '../../../../api/organization/allocationWindowTypes';
import { formatDay, isDay, windowErrorMessage } from '../../../organizations/allocationWindows/windowDraft';
import {
    MAX_PLAN_FILE_BYTES,
    planBodyTooLarge,
    planFieldKey,
    planProblemKey,
    planRefusalKey,
    planTemplateCsv,
} from './planImportFile';
import { readTextFile } from '../../../../utils/textFile';

/** A row's problems differ by code and column; a pair's by code, grant and names. */
const problemKey = (p: PlanImportProblem): string =>
    [p.line, p.code, p.detail, p.grantId, p.organizationName, p.groupName].map((v) => v ?? '').join('|');

const inputClass = 'rounded-md border border-foreground/15 px-2 py-1.5 text-sm bg-text-contrast text-foreground '
    + 'focus:outline-none focus:border-foreground/40';

interface ImportPlanPanelProps {
    /** The tab's season, which rows without their own dates take. */
    season: Season;
    onClose: () => void;
}

/**
 * "Import plan" (W2-3, PO 2026-10-24 Q9–Q15 A): the season's hall plan as a
 * CSV sheet, one window per row. A preview first; the import is all or
 * nothing. Names are matched, never created; a club without access to the
 * room gets shared access on its one running contract, and every such access
 * is listed before anything is written.
 */
const ImportPlanPanel: React.FC<ImportPlanPanelProps> = ({ season: initialSeason, onClose }) => {
    const { t, i18n } = useTranslation();
    const language: 'de' | 'en' = i18n.language?.startsWith('de') ? 'de' : 'en';
    const fileInput = useRef<HTMLInputElement>(null);
    const [season, setSeason] = useState<Season>(initialSeason);
    const [file, setFile] = useState<{ name: string, csv: string } | null>(null);
    const [report, setReport] = useState<{ args: PlanImportArgs, report: PlanImportReport } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState<number | null>(null);
    const [preview, { isLoading: previewing }] = usePreviewPlanImportMutation();
    const [importPlan, { isLoading: importing }] = useImportPlanMutation();

    const seasonValid = isDay(season.from) && isDay(season.to) && season.from <= season.to;
    const args: PlanImportArgs | null = file && seasonValid ? { csv: file.csv, season } : null;
    const current = report && args && JSON.stringify(report.args) === JSON.stringify(args) ? report.report : null;
    const clean = !!current && current.windows.length > 0 && current.problems.length === 0;

    const chooseFile = async (chosen: File | undefined) => {
        setError(null);
        setDone(null);
        setReport(null);
        setFile(null);
        if (!chosen) return;
        if (chosen.size > MAX_PLAN_FILE_BYTES) {
            setError(t('allocation_windows.import.too_large'));
            return;
        }
        let csv: string;
        try {
            csv = await readTextFile(chosen);
        } catch {
            setError(t('allocation_windows.import.unreadable'));
            return;
        }
        if (planBodyTooLarge(csv)) setError(t('allocation_windows.import.too_large'));
        else setFile({ name: chosen.name, csv });
    };

    const runPreview = async () => {
        if (!args) return;
        setError(null);
        setDone(null);
        try {
            setReport({ args, report: await preview(args).unwrap() });
        } catch (err) {
            setReport(null);
            const key = planRefusalKey(err);
            setError(key ? t(key) : windowErrorMessage(err, t('allocation_windows.import.error')));
        }
    };

    const runImport = async () => {
        if (!args) return;
        setError(null);
        try {
            const result = await importPlan(args).unwrap();
            setReport(null);
            setFile(null);
            if (fileInput.current) fileInput.current.value = '';
            setDone(result.windows.length);
        } catch (err) {
            // Something changed since the preview: the refusal carries the new report.
            const details = (err as { data?: { details?: PlanImportReport } } | undefined)?.data?.details;
            if (details) setReport({ args, report: details });
            const key = planRefusalKey(err);
            setError(key ? t(key) : windowErrorMessage(err, t('allocation_windows.import.error')));
        }
    };

    const downloadTemplate = () => {
        const csv = planTemplateCsv(
            ['room', 'organization', 'weekday', 'startTime', 'endTime', 'validFrom', 'validTo']
                .map((field) => t(`allocation_windows.import.field.${field}`)),
            [
                t('allocation_windows.import.example.room'),
                t('allocation_windows.import.example.organization'),
                t('allocation_windows.import.example.weekday'),
                '17:00',
                '19:00',
                '',
                '',
            ],
        );
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = t('allocation_windows.import.template_file');
        link.click();
        URL.revokeObjectURL(url);
    };

    const problemText = (problem: PlanImportProblem): string => {
        const key = planProblemKey(problem);
        const fieldKey = planFieldKey(problem.detail);
        const text = key
            ? t(key, { detail: fieldKey ? t(fieldKey) : problem.detail ?? '' })
            : problem.message;
        if (problem.line !== undefined) return t('allocation_windows.import.line', { line: problem.line, text });
        return `${problem.organizationName || t('common.not_applicable')} · `
            + `${problem.groupName || t('common.not_applicable')}: ${text}`;
    };

    return (
        <div className={'flex flex-col gap-3 rounded-[5px] bg-text-contrast shadow-sm px-5 py-4'}>
            <div>
                <p className={'text-sm font-medium text-foreground'}>{t('allocation_windows.import.title')}</p>
                <p className={'text-sm text-foreground/60'}>{t('allocation_windows.import.hint')}</p>
            </div>
            <div className={'flex flex-wrap items-end gap-3'}>
                <label className={'flex flex-col gap-1 text-xs text-foreground/60'}>
                    {t('allocation_windows.import.file')}
                    <input
                        ref={fileInput}
                        type={'file'}
                        accept={'.csv,text/csv'}
                        className={'text-sm text-foreground'}
                        onChange={(event) => { chooseFile(event.target.files?.[0]); }}
                    />
                </label>
                <label className={'flex flex-col gap-1 text-xs text-foreground/60'}>
                    {t('allocation_windows.import.season_from')}
                    <input
                        type={'date'}
                        className={inputClass}
                        value={season.from}
                        onChange={(event) => setSeason({ ...season, from: event.target.value })}
                    />
                </label>
                <label className={'flex flex-col gap-1 text-xs text-foreground/60'}>
                    {t('allocation_windows.import.season_to')}
                    <input
                        type={'date'}
                        className={inputClass}
                        value={season.to}
                        onChange={(event) => setSeason({ ...season, to: event.target.value })}
                    />
                </label>
                <CustomButton title={t('allocation_windows.import.template')} isFilled={false} onClick={downloadTemplate} />
            </div>

            {current && (
                <div className={'flex flex-col gap-2 text-sm'}>
                    <p className={'text-foreground'}>
                        {t('allocation_windows.import.count', { total: current.windows.length, rows: current.rows })}
                    </p>
                    {current.problems.length > 0 && (
                        <div role={'alert'}>
                            <p className={'font-medium text-foreground'}>{t('allocation_windows.import.problems_title')}</p>
                            <ul className={'list-disc pl-5 text-foreground/80'}>
                                {current.problems.map((problem) => (
                                    <li key={problemKey(problem)}>{problemText(problem)}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {current.newAccess.length > 0 && (
                        <div>
                            <p className={'font-medium text-foreground'}>{t('allocation_windows.import.new_access_title')}</p>
                            <ul className={'list-disc pl-5 text-foreground/80'}>
                                {current.newAccess.map((access) => (
                                    <li key={`${access.organizationName}-${access.groupName}`}>
                                        {`${access.organizationName || t('common.not_applicable')} · `
                                            + `${access.groupName || t('common.not_applicable')}`}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {current.bookingConflicts.length > 0 && (
                        <div>
                            <p className={'font-medium text-foreground'}>
                                {t('allocation_windows.editor.conflicts_title', { total: current.bookingConflicts.length })}
                            </p>
                            <p className={'text-foreground/60'}>{t('allocation_windows.editor.conflicts_hint')}</p>
                            <ul className={'list-disc pl-5 text-foreground/80'}>
                                {current.bookingConflicts.map((conflict) => (
                                    <li key={`${conflict.grantId}-${conflict.orderId}-${conflict.date}`}>
                                        {t('allocation_windows.copy.conflict_line', {
                                            number: conflict.bookingNumber || t('common.not_applicable'),
                                            date: formatDay(conflict.date, language),
                                        })}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {done !== null && (
                <p className={'text-sm text-foreground'}>{t('allocation_windows.import.done', { total: done })}</p>
            )}
            {error && <p role={'alert'} className={'text-sm text-foreground'}>{error}</p>}

            <div className={'flex flex-wrap gap-3'}>
                <CustomButton title={t('common.close')} isFilled={false} onClick={onClose} />
                <CustomButton
                    title={t('allocation_windows.import.preview')}
                    isFilled={false}
                    loading={previewing}
                    disabled={!args}
                    onClick={runPreview}
                />
                <CustomButton
                    title={t('allocation_windows.import.confirm')}
                    loading={importing}
                    disabled={!clean}
                    onClick={runImport}
                />
            </div>
        </div>
    );
};

export default ImportPlanPanel;
